import {
    AuthContext,
    DataTable,
    DataTablePresenter,
    IAuthContextProps,
    IFilterSchema,
    ITableColumn,
    ITableFetchOptions,
    ListProviderGuarantorsRequest,
    ProviderGuarantor,
    ProviderGuarantorSortType,
} from '@enter/core';
import { DataTableCard, PageHeader, PageLayout, PageLayoutRow } from 'components';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useContext } from 'react';
import { Row } from 'react-table';
import { DialogContext, IDialogContextProps } from 'services';
import { IInjectedRootStore, IObservedRootStore, parseSortBy } from 'stores';
import * as S from './guarantors.styles';

interface IGuarantorsProps extends IInjectedRootStore {
    authContext: IAuthContextProps;
    dialogContext: IDialogContextProps;
}

interface IGuarantorsState {
    loading: boolean;
    options: ITableFetchOptions | undefined;
}

const columns: ITableColumn<ProviderGuarantor>[] = [
    {
        id: 'first',
        Header: 'First',
        accessor: 'first',
        sortType: ProviderGuarantorSortType.FIRST,
        Cell: ({ cell }) => <S.Cell>{cell.value}</S.Cell>,
    },
    {
        id: 'middle',
        Header: 'Middle',
        accessor: 'middle',
        sortType: ProviderGuarantorSortType.MIDDLE,
        Cell: ({ cell }) => <S.Cell>{cell.value}</S.Cell>,
        show: false,
    },
    {
        id: 'last',
        Header: 'Last',
        accessor: 'last',
        sortType: ProviderGuarantorSortType.LAST,
        Cell: ({ cell }) => <S.Cell>{cell.value}</S.Cell>,
    },
    {
        id: 'organization',
        Header: 'Organization',
        accessor: 'organization',
        sortType: ProviderGuarantorSortType.ORGANIZATION,
        Cell: ({ cell }) => <S.Cell>{cell.value}</S.Cell>,
        show: false,
    },
    {
        id: 'dob',
        Header: 'Date of Birth',
        accessor: 'dob',
        sortType: ProviderGuarantorSortType.DOB,
        Cell: ({ cell }) => <S.Cell>{cell.value}</S.Cell>,
    },
    {
        id: 'phone',
        Header: 'Phone',
        accessor: 'phone',
        disableSortBy: true,
        Cell: ({ cell }) => <S.Cell>{cell.value}</S.Cell>,
    },
    {
        id: 'email',
        Header: 'Email',
        accessor: 'email',
        disableSortBy: true,
        Cell: ({ cell }) => <S.Cell>{cell.value}</S.Cell>,
        show: false,
    },
    {
        id: 'addresses',
        Header: 'Address',
        accessor: 'addresses',
        disableSortBy: true,
        Cell: ({ row }) => <S.Cell>{formatAddresses(row.original)}</S.Cell>,
    },
];

const filterSchemas: IFilterSchema<keyof ListProviderGuarantorsRequest>[] = [
    {
        label: 'First name',
        filterId: 'filterFirst',
        columnId: 'first',
        filterType: 'String',
    },
    {
        label: 'Last name',
        filterId: 'filterLast',
        columnId: 'last',
        filterType: 'String',
    },
    {
        label: 'Email',
        filterId: 'filterEmail',
        columnId: 'email',
        filterType: 'String',
    },
];

const formatAddresses = (guarantor: ProviderGuarantor): string[] => {
    return _.map(guarantor.addresses, address => {
        const { lines, city, state, postal, country } = address;
        return _.compact([...lines, city, state, postal, country]).join(' ');
    });
};

class GuarantorsPage extends React.Component<IGuarantorsProps, IGuarantorsState> {
    private presenter: DataTablePresenter<ProviderGuarantor>;

    constructor(props: IGuarantorsProps) {
        super(props);
        this.state = {
            loading: false,
            options: undefined,
        };
        this.presenter = new DataTablePresenter<ProviderGuarantor>({
            name: 'guarantors',
            data: [],
            columns,
            filterSchemas,
        });
    }

    public componentDidMount(): void {
        document.title = 'ENTER Platform - Guarantors';
    }
    public componentWillUnmount(): void {
        this.authContext.cancelGroup('guarantors');
    }

    public render() {
        return (
            <PageLayout>
                <PageHeader title="Guarantors" hideDateRange={true} />
                <PageLayoutRow expand={true}>
                    <DataTableCard size="large">
                        <DataTable
                            presenter={this.presenter}
                            isLoading={this.state.loading}
                            onFetch={this.onFetch}
                            getRowHref={this.getRowHref}
                        />
                    </DataTableCard>
                </PageLayoutRow>
            </PageLayout>
        );
    }

    private getRowHref = (row: Row<ProviderGuarantor>): string => {
        const getAccountId = (): string | undefined => {
            const pathParts = window.location.pathname.split('/').filter(Boolean);
            return pathParts.length > 0 && pathParts[0].length === 36 ? pathParts[0] : undefined;
        };
        const accountId = getAccountId();
        return accountId ? `/${accountId}/guarantors/${row.original.id}` : `/guarantors/${row.original.id}`;
    };

    private get authContext(): IAuthContextProps {
        return this.props.authContext;
    }

    private get dialogContext(): IDialogContextProps {
        return this.props.dialogContext;
    }

    private onFetch = async (fetchOptions: ITableFetchOptions): Promise<void> => {
        try {
            this.setState({
                options: fetchOptions,
                loading: true,
            });
            this.authContext.cancelGroup('guarantors');
            this.authContext.setGroup('guarantors');

            const { pageIndex, pageSize, sortBy, validFilters } = fetchOptions;
            const sortAndOrder = parseSortBy(sortBy, columns);
            const listing = await this.props.rootStore.guarantorStore.loadGuarantorListing({
                page: pageIndex,
                pageSize,
                ...sortAndOrder,
                filters: validFilters,
            });
            this.presenter.onDataUpdate(listing.items || []);
            this.presenter.onDataTotalChange(listing.total || 0);
            this.setState({
                loading: false,
            });
        } catch (err) {
            this.dialogContext.showError(err);
        }
    };
}

export const Guarantors: React.FC<IObservedRootStore> = inject('rootStore')(
    observer((props: IObservedRootStore) => {
        const { rootStore } = props;
        if (!rootStore) {
            return <></>;
        }
        const authContext = useContext(AuthContext);
        const dialogContext = useContext(DialogContext);
        return <GuarantorsPage authContext={authContext} dialogContext={dialogContext} rootStore={rootStore} />;
    }),
);
